import React, { useMemo, useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import styled from 'styled-components';
import Spinner from "./Spinner";

// Hook to detect mobile screen size
const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);

        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    return isMobile;
};

// Mobile card component for responsive display
const MobileCard = ({ row, columns, index }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const getDisplayValue = (column, row) => {
        if (column.cell) {
            return column.cell(row, index);
        }
        return column.selector ? column.selector(row) : '';
    };

    const getFirstColumn = () => columns.find(col => !col.hide || col.hide !== 'sm');
    const getOtherColumns = () => columns.filter(col => col !== getFirstColumn());

    const firstColumn = getFirstColumn();
    const otherColumns = getOtherColumns();

    return (
        <div className="mobile-card">
            <div
                className="mobile-card-header"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div>
                    <div className="font-semibold text-gray-900">
                        {firstColumn ? getDisplayValue(firstColumn, row) : 'No data'}
                    </div>
                    {otherColumns.slice(0, 1).map(column => (
                        <div key={column.name} className="text-sm text-gray-500 mt-1">
                            {column.name}: {getDisplayValue(column, row)}
                        </div>
                    ))}
                </div>
                <div className="flex items-center">
                    <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </div>

            <div className={`mobile-card-content ${isExpanded ? 'expanded' : ''}`}>
                {otherColumns.slice(1).map(column => (
                    <div key={column.name} className="mobile-card-field">
                        <span className="mobile-card-label">{column.name}</span>
                        <span className="mobile-card-value">
                            {getDisplayValue(column, row)}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const TextField = styled.input`
    height: 40px;
    width: 100%;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    padding: 0 16px;
    font-size: 0.875rem;
    transition: all 0.2s ease-in-out;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    background: white;

    &:focus {
        outline: none;
        border-color: var(--primary-color);
        box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
    }

    &:hover {
        border-color: #cbd5e1;
        cursor: text;
    }

    &::placeholder {
        color: #94a3b8;
    }

    @media (min-width: 640px) {
        width: 240px;
    }
`;

const StyledDataTable = styled(DataTable)`
    .rdt_Table {
        border-radius: 8px;
        overflow: hidden;
    }

    .rdt_TableRow {
        border-bottom: 1px solid #f1f5f9;
        transition: background-color 0.15s ease;
    }

    .rdt_TableRow:hover {
        background-color: #f8fafc;
    }

    .rdt_TableHeadRow {
        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        border-bottom: 1px solid #e2e8f0;
        position: relative;
        font-weight: 600;
        color: #374151;
    }

    .rdt_TableHeadRow::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
        opacity: 0.8;
    }

    .rdt_TableHeader {
        font-size: 0.875rem;
        font-weight: 600;
        color: #374151;
        padding: 16px 12px;
    }

    .rdt_TableCell {
        padding: 12px;
        font-size: 0.875rem;
        color: #1f2937;
    }

    .rdt_Pagination {
        border-top: 1px solid #f1f5f9;
        background: #fafbfc;
    }
`;

const FilterComponent = ({ filterText, onFilter, onClear }) => ( 
    <>
        <TextField
            id="search"
            type="text"
            placeholder="Search"
            aria-label="Search Input"
            value={filterText}
            onChange={onFilter}
        />
        {/* <button type="button" onClick={onClear}>
            X
        </button> */}
    </>
);

const MainTableV2 = ({
    columns,
    data,
    primaryColor,
    loading,
    alertMessage,
    clearAlert,
    title,
    searchQuery,
    setSearchQuery,
    filterConfig,
    children,
    dashboard, // New prop for complex dashboard content
    isNested = false
}) => {
    const [filterText, setFilterText] = useState(searchQuery);
    const isMobile = useIsMobile();
    // console.log('Filtered Text: ', filterText);

    useEffect(() => {
        setSearchQuery(filterText); // Sync external search query changes with internal state
    }, [filterText, setSearchQuery]);

    useEffect(() => {
        setFilterText(searchQuery); // Sync internal state with external changes
    }, [searchQuery]);    

    const filteredItems = useMemo(() => {
        if (!searchQuery) return data;

        return data.filter(item => {
            return filterConfig.some(config => {
                let value;
                if (isNested && item.cardGroup) {
                    value = item.cardGroup[config.key];
                } else {
                    value = item[config.key];
                }
                if (!value) return false;

                const valueStr = config.type === 'number' ? value.toString() : value.toLowerCase();
                const queryStr = searchQuery.toLowerCase();

                return valueStr.includes(queryStr);
            });
        });
    }, [data, searchQuery, filterConfig, isNested]);
    

    const subHeaderComponentMemo = useMemo(() => {
        const handleClear = () => {
            if (filterText) {
                setFilterText('');
                setSearchQuery('');
            }
        };

        return <FilterComponent onFilter={e => setFilterText(e.target.value)} onClear={handleClear} filterText={filterText} />;
    }, [filterText, setSearchQuery]);

    return (
        <>
            {loading && (
                <div className="fixed inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75">
                    <Spinner />
                </div>
            )}
            {alertMessage && (
                <div
                    className={`alert p-4 rounded-lg mb-4 ${
                        alertMessage.startsWith("Successfully")
                            ? "bg-green-50 text-green-700 border border-green-200"
                            : "bg-red-50 text-red-700 border border-red-200"
                    } flex justify-between items-center shadow-sm`}
                >
                    {alertMessage}
                    <button onClick={clearAlert} className="text-lg font-bold hover:opacity-70">
                        ×
                    </button>
                </div>
            )}

            <div className="space-y-6">
                {/* Header Section */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                    <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
                </div>

                {/* Dashboard Section - Full width for analytics/complex content */}
                {dashboard && (
                    <div className="w-full dashboard-section">
                        {dashboard}
                    </div>
                )}

                {/* Controls Bar - Search + Action Buttons */}
                {(subHeaderComponentMemo || children) && (
                    <div className="controls-bar">
                        {/* Search Section */}
                        <div className="controls-search">
                            {subHeaderComponentMemo}
                        </div>

                        {/* Action Buttons Section */}
                        {children && (
                            <div className="controls-actions">
                                {children}
                            </div>
                        )}
                    </div>
                )}

                {/* Data Display Section */}
                <div className="table-card">
                    {isMobile ? (
                        // Mobile Card View
                        <div className="p-4">
                            {filteredItems.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <p className="text-sm">No data available</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {filteredItems.map((row, index) => (
                                        <MobileCard
                                            key={index}
                                            row={row}
                                            columns={columns}
                                            index={index}
                                        />
                                    ))}
                                    {/* Mobile Pagination Placeholder */}
                                    {filteredItems.length > 10 && (
                                        <div className="text-center py-4 text-sm text-gray-500">
                                            Showing {filteredItems.length} items
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        // Desktop Table View
                        <StyledDataTable
                            columns={columns}
                            data={filteredItems}
                            primaryColor={primaryColor}
                            responsive={false}
                            pagination
                            paginationResetDefaultPage={filterText.length > 0}
                            persistTableHead
                            highlightOnHover
                            noHeader={false}
                            customStyles={{
                                table: {
                                    style: {
                                        backgroundColor: 'white',
                                    },
                                },
                                headCells: {
                                    style: {
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        color: '#374151',
                                        paddingLeft: '12px',
                                        paddingRight: '12px',
                                    },
                                },
                                cells: {
                                    style: {
                                        fontSize: '0.875rem',
                                        color: '#1f2937',
                                        paddingLeft: '12px',
                                        paddingRight: '12px',
                                    },
                                },
                                pagination: {
                                    style: {
                                        borderTopLeftRadius: '0',
                                        borderTopRightRadius: '0',
                                        borderBottomLeftRadius: '12px',
                                        borderBottomRightRadius: '12px',
                                        backgroundColor: '#fafbfc',
                                        borderTop: '1px solid #f1f5f9',
                                    },
                                }
                            }}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default MainTableV2;

